import { useChartStore } from '@/store/chartStore';
import { Modal } from '@/components/ui/Modal';
import { Z_INDEX } from '@/constants/zIndex';
import type { ForwardEpsModel } from '@/hooks/useEpsLine';

const FORWARD_MODELS: { key: ForwardEpsModel; label: string; desc: string }[] = [
  { key: 'ttm-yoy',           label: 'TTM YoY Growth',     desc: 'Project next-quarter EPS using trailing 12-month YoY growth applied to latest quarter' },
  { key: 'cagr-2y',          label: '2-Year CAGR',         desc: 'Smooth 2-year CAGR applied to latest quarter EPS' },
  { key: 'quarter-momentum', label: 'Quarterly Momentum',  desc: 'Apply latest quarter year-over-year growth rate to project next quarter' },
];

interface Props {
  isActive: boolean;
  onClose: () => void;
  onAdd?: () => void;
}

function triggerRedraw() {
  const chart = useChartStore.getState().chart;
  const paneId = useChartStore.getState().indicatorPaneMap.get('EPS_LINE');
  if (chart && paneId) {
    try {
      (chart as unknown as { overrideIndicator: (o: { name: string }, paneId?: string) => void })
        .overrideIndicator({ name: 'EPS_LINE' }, paneId);
    } catch { /* ignore */ }
  }
}

export function EpsLineConfigModal({ isActive, onClose, onAdd }: Props) {
  const epsLineAlignToAnnouncement = useChartStore((s) => s.epsLineAlignToAnnouncement);
  const setEpsLineAlignToAnnouncement = useChartStore((s) => s.setEpsLineAlignToAnnouncement);
  const epsLineAxisScale = useChartStore((s) => s.epsLineAxisScale);
  const setEpsLineAxisScale = useChartStore((s) => s.setEpsLineAxisScale);
  const epsLineForwardModel = useChartStore((s) => s.epsLineForwardModel);
  const setEpsLineForwardModel = useChartStore((s) => s.setEpsLineForwardModel);
  const epsLineShowForwardProjection = useChartStore((s) => s.epsLineShowForwardProjection);
  const setEpsLineShowForwardProjection = useChartStore((s) => s.setEpsLineShowForwardProjection);
  const setEpsLineEnabled = useChartStore((s) => s.setEpsLineEnabled);

  function handleRemove() {
    setEpsLineEnabled(false);
    const chart = useChartStore.getState().chart;
    const paneMap = useChartStore.getState().indicatorPaneMap;
    if (chart && paneMap.has('EPS_LINE')) {
      try {
        (chart as unknown as { removeIndicator: (paneId: string, name?: string) => void })
          .removeIndicator('candle_pane', 'EPS_LINE');
      } catch { /* ignore */ }
      const next = new Map(paneMap);
      next.delete('EPS_LINE');
      useChartStore.getState().setIndicatorPaneMap(next);
    }
    onClose();
  }

  return (
    <Modal onClose={onClose} size="xl" zIndex={Z_INDEX.modalElevated} className="max-h-[min(90vh,680px)]">
      <Modal.Header
        onClose={onClose}
        title={
          <div className="flex items-center gap-2.5">
            <span className="text-base font-bold text-modal-text">EPS Line</span>
            <span className="text-sm text-modal-muted">— Raw EPS on secondary axis</span>
            {isActive && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-accent/15 text-accent font-medium">
                Active
              </span>
            )}
          </div>
        }
      />

      {/* Body */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
          {/* Left: Settings */}
          <div className="w-[55%] border-r border-border flex flex-col overflow-y-auto">
            <div className="px-5 py-4 flex flex-col gap-4">

              {/* Axis scale */}
              <p className="text-xs font-semibold text-modal-muted uppercase tracking-wider">Axis Scale</p>
              <div className="flex items-center gap-2">
                {(['linear', 'log'] as const).map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => { setEpsLineAxisScale(s); triggerRedraw(); }}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                      epsLineAxisScale === s
                        ? 'bg-accent/15 border-accent/40 text-accent'
                        : 'bg-transparent border-border text-modal-muted hover:bg-modal-hover'
                    }`}
                  >
                    {s === 'linear' ? 'Linear' : 'Logarithmic'}
                  </button>
                ))}
              </div>
              <p className="text-xs text-modal-muted -mt-2">
                {epsLineAxisScale === 'log'
                  ? 'Symmetric log scale — useful when EPS spans multiple orders of magnitude'
                  : 'Linear scale — equal spacing between EPS values'}
              </p>

              <div className="h-px bg-border" />

              {/* Alignment */}
              <p className="text-xs font-semibold text-modal-muted uppercase tracking-wider">Alignment</p>
              <div className="flex items-center gap-4">
                <button
                  type="button"
                  role="switch"
                  aria-checked={epsLineAlignToAnnouncement}
                  onClick={() => {
                    setEpsLineAlignToAnnouncement(!epsLineAlignToAnnouncement);
                    triggerRedraw();
                  }}
                  className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${
                    epsLineAlignToAnnouncement ? 'bg-accent' : 'bg-border'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${
                      epsLineAlignToAnnouncement ? 'translate-x-4' : 'translate-x-1'
                    }`}
                  />
                </button>
                <div className="flex flex-col">
                  <span className="text-sm text-modal-text font-medium">Align to announcement date</span>
                  <span className="text-xs text-modal-muted">
                    {epsLineAlignToAnnouncement
                      ? 'Points plotted on the date results were announced'
                      : 'Points plotted on the fiscal quarter-end date'}
                  </span>
                </div>
              </div>

              <div className="h-px bg-border" />

              {/* Forward projection */}
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-modal-muted uppercase tracking-wider">Forward Projection</p>
                <button
                  type="button"
                  role="switch"
                  aria-checked={epsLineShowForwardProjection}
                  onClick={() => { setEpsLineShowForwardProjection(!epsLineShowForwardProjection); triggerRedraw(); }}
                  className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${
                    epsLineShowForwardProjection ? 'bg-accent' : 'bg-border'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${
                      epsLineShowForwardProjection ? 'translate-x-4' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              <div className={`flex flex-col gap-2 ${epsLineShowForwardProjection ? '' : 'opacity-40 pointer-events-none'}`}>
                {FORWARD_MODELS.map((m) => (
                  <label
                    key={m.key}
                    className={`flex items-start gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors ${
                      epsLineForwardModel === m.key
                        ? 'bg-accent/10 border-accent/40'
                        : 'bg-transparent border-border hover:bg-modal-hover'
                    }`}
                  >
                    <input
                      type="radio"
                      name="eps-line-forward-model"
                      checked={epsLineForwardModel === m.key}
                      onChange={() => { setEpsLineForwardModel(m.key); triggerRedraw(); }}
                      className="mt-0.5 accent-accent shrink-0"
                    />
                    <div className="flex flex-col">
                      <span className="text-sm text-modal-text font-medium">{m.label}</span>
                      <span className="text-xs text-modal-muted leading-snug">{m.desc}</span>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Right: About */}
          <div className="flex-1 overflow-y-auto px-5 py-4 flex flex-col gap-4">
            <p className="text-xs font-semibold text-modal-muted uppercase tracking-wider">About</p>
            <p className="text-sm text-modal-text leading-relaxed">
              The EPS Line plots raw quarterly earnings per share (EPS) on a secondary y-axis drawn
              on the left of the price pane. The axis range is computed from the EPS values
              visible in the current viewport and updates dynamically as you scroll.
            </p>
            <p className="text-sm text-modal-text leading-relaxed">
              Green dots and lines represent profitable quarters; red dots with a faint dotted
              trajectory represent loss quarters. Each point is annotated with its YoY EPS growth
              and revenue growth percentages.
            </p>

            <div className="h-px bg-border" />

            <p className="text-xs font-semibold text-modal-muted uppercase tracking-wider">Legend</p>
            <div className="flex flex-col gap-1 text-xs text-modal-muted">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-green-400 shrink-0" />
                <span>Positive EPS quarter</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-red-400 shrink-0" />
                <span>Loss quarter (EPS ≤ 0)</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-0.5 border-t border-dashed border-green-400 shrink-0" />
                <span>Projected next quarter (forward model estimate)</span>
              </div>
            </div>

            <div className="h-px bg-border" />

            <p className="text-xs font-semibold text-modal-muted uppercase tracking-wider">Axis</p>
            <p className="text-sm text-modal-text leading-relaxed">
              The secondary axis on the left edge of the price pane shows the EPS scale. The
              "LIN" or "LOG" badge at the bottom of the axis indicates the current scale mode.
              The axis range pads the visible EPS by 18% above and below for clarity.
            </p>
          </div>
        </div>

      <Modal.Footer className="!justify-between">
        {isActive ? (
          <button
            type="button"
            onClick={handleRemove}
            className="text-sm px-3 py-2 rounded-lg border border-red-500/40 text-red-400 hover:bg-red-500/10 transition-colors"
          >
            Remove Indicator
          </button>
        ) : (
          <span />
        )}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onClose}
            className="text-sm px-3 py-2 rounded-lg text-modal-muted hover:text-modal-text hover:bg-modal-hover transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={isActive || !onAdd ? onClose : () => { onAdd(); onClose(); }}
            className="text-sm px-4 py-2 rounded-lg bg-accent text-white font-medium hover:bg-accent/90 transition-colors"
          >
            {isActive ? 'Done' : 'Add to Chart'}
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
